"use client";
import React, { useState, useEffect } from "react";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import ContactAlert from "../../Components/Alerts/ContactAlert";
import {
	sendFormEvent,
	sendLeadGenerationEvent,
} from "@/app/_ga4/eventHandler";
import { EmailInput, PhoneInput, TextareaInput, TextInput } from "@/app/_components/FormComponents/FormInputs";

export default function ContactForm() {
	const [formStarted, setFormStarted] = useState(false);
	const initState = {
		origin: "FC_Website",
		fname: "",
		phone: "",
		email: "",
		comment: "",
		ip: null,
	};
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [formData, setFormData] = useState(initState);
	const [errors, setErrors] = useState({});
	const [showAlert, setShowAlert] = useState(false);
	const [alertStatus, setAlertStatus] = useState("");
	const [alertMessage, setAlertMessage] = useState("");

	useEffect(() => {
		async function fetchClientIp() {
			try {
				const response = await fetch(
					"https://api64.ipify.org?format=json"
				);
				const data = await response.json();
				// Update the form data directly with the client IP
				setFormData((prevData) => ({
					...prevData,
					ip: data.ip,
				}));
			} catch (error) {
				console.error("Error fetching client IP:", error);
			}
		}
		fetchClientIp();
	}, []);


	const validateForm = () => {
		const fullNameRegex = /^[a-zA-Z\s'-]+$/;
		const newErrors = {};

		// Full Name Validation
		if (!formData.fname) {
			newErrors.fname = "Full name is required";
		} else if (!fullNameRegex.test(formData.fname)) {
			newErrors.fname = "Full name must only contain letters and spaces";
		} else if (formData.fname.length > 100) {
			newErrors.fname = "Full name must not exceed 100 characters";
		}

		// Phone Validation
		if (!formData.phone) {
			newErrors.phone = "Phone is required";
		} else {
			const phoneNumber = parsePhoneNumberFromString(formData.phone, 'US'); // Default to US country code
			if (!phoneNumber || !phoneNumber.isValid()) {
				newErrors.phone = "Invalid phone number";
			}
		}

		// Email Validation
		if (!formData.email) {
			newErrors.email = "Email is required";
		} else {
			const emailRegex =
				/^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
			if (!emailRegex.test(formData.email)) {
				newErrors.email = "Invalid email address";
			}
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const addContactToLeadDatabase = async (data, form) => {
		data.form = form;
		data.source = "FC_Website";
		try {
			let dbResponse = await fetch("/api/submit-lead", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(data),
			});
			let response = await dbResponse.json();
			return response;
		} catch (error) {
			console.log(error);
		}
	};
	const handleChange = (e) => {
		if (!formStarted) {
			if (process.env.NODE_ENV !== "development") {
				sendFormEvent("CONTACT_FORM", "START");
			}
			setFormStarted(true);
		}

		const { name, value } = e.target;

		// For phone number, format it as you type
		if (name === 'phone') {
			const formattedPhone = formatPhoneNumber(value);
			setFormData((prevData) => ({
				...prevData,
				[name]: formattedPhone,
			}));
		} else {
			setFormData((prevData) => ({
				...prevData,
				[name]: value,
			}));
		}
	};

	// Function to format the phone number as the user types
	const formatPhoneNumber = (input) => {
		const phoneNumber = parsePhoneNumberFromString(input, 'US'); // 'US' is the default country code

		if (phoneNumber && phoneNumber.isValid()) {
			return phoneNumber.formatInternational();
		}
		return input;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (validateForm()) {
			setIsSubmitting(true);
			let databaseSubmission = await addContactToLeadDatabase(formData, "contact_form");
			if (process.env.NODE_ENV !== "development") {
				sendFormEvent("CONTACT_FORM", "SUBMIT");
			}
			let request = await submitLead();
			if (request.success) {
				setAlertStatus("Success");
				setShowAlert(true);
				setAlertMessage(request.data);
				setFormData(initState);
				setIsSubmitting(false);
			} else {
				setAlertStatus("Failed");
				setAlertMessage(request.message);
				setShowAlert(true);
				setIsSubmitting(false);
			}
		}
	};

	const submitLead = async () => {
		try {
			let response = await fetch("/api/contact", {
				headers: {
					"Content-Type": "application/json",
				},
				method: "POST",
				body: JSON.stringify(formData),
			});
			response = await response.json();
			if (response.success) {
				setAlertStatus("Success");
				setShowAlert(true);
				setAlertMessage(response.message);
				if (process.env.NODE_ENV !== "development") {
					sendLeadGenerationEvent("CONTACT_FORM");
				}
				return response;
			} else {
				setAlertStatus("Failed");
				if (process.env.NODE_ENV !== "development") {
					sendFormEvent("CONTACT_FORM", "INVALID/ERROR");
				}
				setShowAlert(true);
				setAlertMessage(response.message);
				return response;
			}
		} catch (error) {
			return { success: false, message: "Error submitting request" };
		}
	};
	const handleAutoFill = (e) => {
		e.preventDefault();
		setFormData({
			fname: "DELETE ME",
			phone: "8145380204",
			email: "lauer.chris1@gmail.com",
			comment: "This is a test comment",
			ip: formData.ip, // Retain the fetched IP
		});
	};
	const closeAlert = () => {
		setShowAlert(false);
	};

	return (
		<>
			{showAlert && (
				<ContactAlert
					showAlert={showAlert}
					alertMessage={alertMessage}
					alertStatus={alertStatus}
					closeAlert={closeAlert}
				/>
			)}
			<form className='register-form' onSubmit={handleSubmit}>
				{process.env.NODE_ENV === "development" && (
					<button className='dev-button' onClick={handleAutoFill}>
						Log Form Data
					</button>
				)}
				<TextInput
					label='Full Name'
					name='fname'
					value={formData.fname}
					error={errors.fname}
					onChange={handleChange}
					placeholder='Full Name'
				/>
				<EmailInput
					label='Email'
					name='email'
					value={formData.email}
					error={errors.email}
					onChange={handleChange}
					placeholder={"your@email.com"}
				/>
				<PhoneInput
					label='Phone'
					name='phone'
					value={formData.phone}
					error={errors.phone}
					onChange={handleChange}
					placeholder='123-456-7890'
				/>
				<TextareaInput
					label='Comment'
					name='comment'
					value={formData.comment}
					error={errors.comment}
					onChange={handleChange}
					placeholder='How can we help you?'
				/>

				<div className='button-wrapper'></div>
				<button type="submit"
					className="form-submit"
					disabled={isSubmitting}>
					{isSubmitting ? "Processing..." : "Submit"}
				</button>
			</form>
		</>
	);
}
